import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  MdHome,
  MdPerson,
  MdLogin,
  MdBusiness,
  MdCategory,
  MdLocalOffer,
  MdStorefront,
  MdPlace,
  MdShoppingCart,
  MdLayers,
} from "react-icons/md";

import classes from "./Bar.module.sass";
import { selectCurrentUser } from "../../../store/slices/currentUserSlice";
import { selectCity } from "../../../store/slices/citySlice";
import { selectCartItems } from "../../../store/slices/cartSlice";
import { setCommonModalState } from "../../../store/slices/commonModalSlice";
import { CommonModals } from "../../types/enums";
import {
  MAIN_ROUTE,
  CLUSTERS_ROUTE,
  CATEGORIES_ROUTE,
  ITEMS_ROUTE,
  PROMOS_ROUTE,
  LOGIN_ROUTE,
  CART_ROUTE,
  COMPLEX_CART_ROUTE,
  PROFILE_ROUTE,
} from "../../utils/constants";

const NAV_ITEMS = [
  { path: ITEMS_ROUTE, label: "ТОВАРЫ", icon: MdStorefront },
  { path: CATEGORIES_ROUTE, label: "КАТЕГОРИИ", icon: MdCategory },
  { path: CLUSTERS_ROUTE, label: "КОМПАНИИ", icon: MdBusiness },
  { path: PROMOS_ROUTE, label: "АКЦИИ", icon: MdLocalOffer },
];

export const Bar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const selectedCity = useSelector(selectCity);
  const cartItems = useSelector(selectCartItems);
  const complexItems = useSelector(
    (state: any) => state.complexOrderCart.items,
  );

  const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);
  const complexCount = complexItems.reduce(
    (s: number, i: any) => s + i.quantity,
    0,
  );

  const isMain = location.pathname === MAIN_ROUTE;
  const isLogin = location.pathname === LOGIN_ROUTE;

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const openCityModal = () =>
    dispatch(
      setCommonModalState({ modal: CommonModals.CITY_SELECT, state: true }),
    );

  return (
    <header className={classes.Container}>
      <div className={classes.LeftSide}>
        <button
          type="button"
          className={classes.IconButton}
          data-active={isMain ? "true" : undefined}
          onClick={() => navigate(MAIN_ROUTE)}
          aria-label="На главную"
        >
          <MdHome size={22} />
        </button>
        {currentUser && (
          <button
            type="button"
            className={classes.IconButton}
            data-active={isActive(PROFILE_ROUTE) ? "true" : undefined}
            onClick={() => navigate(PROFILE_ROUTE)}
            aria-label="Профиль"
          >
            <MdPerson size={22} />
          </button>
        )}
      </div>

      <nav className={classes.Nav}>
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              type="button"
              className={classes.NavItem}
              data-active={active ? "true" : undefined}
              onClick={() => navigate(item.path)}
              aria-label={item.label}
            >
              <span className={classes.NavLabel}>{item.label}</span>
              <Icon className={classes.NavIcon} size={22} />
            </button>
          );
        })}
      </nav>

      <div className={classes.RightSide}>
        <button
          type="button"
          className={classes.CityButton}
          onClick={openCityModal}
          aria-label="Выбрать город"
        >
          <MdPlace size={18} />
          <span className={classes.CityLabel}>
            {selectedCity?.value || "Город"}
          </span>
        </button>

        <button
          type="button"
          className={classes.CartButton}
          data-active={isActive(CART_ROUTE) ? "true" : undefined}
          onClick={() => navigate(CART_ROUTE)}
          aria-label="Корзина"
        >
          <MdShoppingCart size={22} />
          {cartCount > 0 && (
            <span className={classes.CartBadge}>{cartCount}</span>
          )}
        </button>

        <button
          type="button"
          className={classes.CartButton}
          data-active={isActive(COMPLEX_CART_ROUTE) ? "true" : undefined}
          onClick={() => navigate(COMPLEX_CART_ROUTE)}
          aria-label="Комплексы"
        >
          <MdLayers size={22} />
          {complexCount > 0 && (
            <span className={classes.CartBadge}>{complexCount}</span>
          )}
        </button>

        {!currentUser && (
          <button
            type="button"
            className={classes.IconButton}
            data-active={isLogin ? "true" : undefined}
            onClick={() => navigate(LOGIN_ROUTE)}
            aria-label="Войти"
          >
            <MdLogin size={22} />
          </button>
        )}
      </div>
    </header>
  );
};
